import {
  useGetHeliosUserFiltersQuery,
  useCreateHeliosUserFilterMutation,
  useUpdateHeliosUserFilterMutation,
  useDeleteHeliosUserFilterMutation,
} from 'store/services/helios/userFilters';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ConditionValueSource, HeliosRecordSystemDatasource, LogicalOperator, TemplatePresentationItemDisplay } from 'types/dataHelios';
import {
  EUserFilterType,
  EUserFilterAdministrationSectionType,
  type HeliosUserFilter,
  type HeliosUserFilterCondition,
} from 'types/heliosUserFilters';
import type { FilterCondition } from 'types/tableFilters';
import type { HeliosRecordDataTablesColumn } from 'types/dataHelios';
import type { ComparisonOperator } from 'types/dataHelios';

const toSavedCondition = (filter: FilterCondition, column: HeliosRecordDataTablesColumn | undefined): HeliosUserFilterCondition => ({
  leftBracket: filter.leftBracket || false,
  rightBracket: filter.rightBracket || false,
  isBlank: filter.isBlank ?? null,
  datasource: {
    templateDataItemId: column?.aggregationGroupId ? undefined : (column?.itemId ?? 0),
    aggregationGroupId: column?.aggregationGroupId ?? undefined,
    systemDatasource: HeliosRecordSystemDatasource.HeliosUser,
  },
  leftLogicalOperator: filter.logicalOperator,
  comparisonOperator: filter.operator,
  value: {
    conditionValueSource: ConditionValueSource.None,
    staticValue: filter.value || undefined,
    staticValues: filter.staticValues && filter.staticValues.length > 0 ? filter.staticValues : undefined,
    fromValue: filter.fromValue || undefined,
    toValue: filter.toValue || undefined,
  },
});

interface UseHeliosFiltersProps {
  templateViewId: string;
  setPagination: (pagination: { page: number; pageSize: number }) => void;
  pageSize: number;
}

export const useHeliosFilters = ({ templateViewId, setPagination, pageSize }: UseHeliosFiltersProps) => {
  const { t } = useTranslation();
  const [activeFilters, setActiveFilters] = useState<FilterCondition[]>([]);
  const [activeSavedFilterId, setActiveSavedFilterId] = useState<string | null>(null);

  const { data: savedFiltersData } = useGetHeliosUserFiltersQuery(
    {
      templateId: templateViewId,
      page: 1,
      pageSize: 100,
      orderBy: 'name' as keyof null,
      orderDesc: false,
      search: '',
    },
    { skip: !templateViewId }
  );
  const [createUserFilter, { isLoading: isSavingFilter }] = useCreateHeliosUserFilterMutation();
  const [updateUserFilter, { isLoading: isUpdatingFilter }] = useUpdateHeliosUserFilterMutation();
  const [deleteUserFilter] = useDeleteHeliosUserFilterMutation();

  const savedFilters = useMemo(() => savedFiltersData?.items || [], [savedFiltersData?.items]);
  const isSavingOrUpdating = isSavingFilter || isUpdatingFilter;

  const handleSaveFilter = useCallback(
    async (name: string, isDefault: boolean, columns: HeliosRecordDataTablesColumn[] | undefined) => {
      try {
        const conditions = activeFilters.map((filter) =>
          toSavedCondition(
            filter,
            columns?.find((c) => c.columnKey === filter.columnKey)
          )
        );

        await createUserFilter({
          name,
          conditions,
          isActive: true,
          isDefault,
          userFilterType: EUserFilterType.Template,
          userFilterAdministrationSectionType: EUserFilterAdministrationSectionType.Listing,
          templateId: templateViewId,
        }).unwrap();

        toast.success(t('helios.templates.filters.filterSaved'));
      } catch {
        toast.error(t('common.error'));
      }
    },
    [activeFilters, createUserFilter, templateViewId, t]
  );

  const handleUpdateFilter = useCallback(
    async (name: string, isDefault: boolean, columns: HeliosRecordDataTablesColumn[] | undefined, explicitConditions?: FilterCondition[]) => {
      if (!activeSavedFilterId) return;

      const filtersToSave = explicitConditions || activeFilters;

      try {
        const conditions = filtersToSave.map((filter) =>
          toSavedCondition(
            filter,
            columns?.find((c) => c.columnKey === filter.columnKey)
          )
        );

        await updateUserFilter({
          id: activeSavedFilterId,
          name,
          isDefault,
          conditions,
        }).unwrap();

        toast.success(t('helios.templates.filters.filterSaved'));
      } catch {
        toast.error(t('common.error'));
      }
    },
    [activeFilters, activeSavedFilterId, updateUserFilter, t]
  );

  const handleDeleteSavedFilter = useCallback(
    async (filterId: string) => {
      try {
        await deleteUserFilter({ ids: [filterId] }).unwrap();
        toast.success(t('helios.templates.filters.filterDeleted'));
      } catch {
        toast.error(t('common.error'));
      }
    },
    [deleteUserFilter, t]
  );

  const handleLoadFilter = useCallback((filter: HeliosUserFilter, columns: HeliosRecordDataTablesColumn[] | undefined): FilterCondition[] => {
    if (!filter.conditions) return [];

    const loadedFilters: FilterCondition[] = filter.conditions.map((condition, index) => {
      const col =
        columns?.find((c) => c.aggregationGroupId && c.aggregationGroupId === condition.datasource?.aggregationGroupId) ||
        columns?.find((c) => c.itemId === condition.datasource?.templateDataItemId);
      return {
        id: `loaded-${index}-${Date.now()}`,
        columnKey: col?.columnKey || '',
        columnName: col?.label || '',
        dataType: (col?.dataType || 'String') as FilterCondition['dataType'],
        displayType: col?.displayType || TemplatePresentationItemDisplay.Value,
        operator: condition.comparisonOperator as unknown as ComparisonOperator,
        value: condition.value?.staticValue || '',
        leftBracket: condition.leftBracket,
        rightBracket: condition.rightBracket,
        isBlank: condition.isBlank ?? null,
        staticValues: condition.value?.staticValues,
        fromValue: condition.value?.fromValue,
        toValue: condition.value?.toValue,
        logicalOperator: condition.leftLogicalOperator || LogicalOperator.And,
      };
    });

    setActiveSavedFilterId(filter.id);
    return loadedFilters;
  }, []);

  const handleUnloadFilter = useCallback(() => {
    setActiveSavedFilterId(null);
  }, []);

  const handleApplyFilters = useCallback(
    (filters: FilterCondition[]) => {
      setActiveFilters(filters);
      setPagination({
        page: 1,
        pageSize,
      });
    },
    [setPagination, pageSize]
  );

  useEffect(() => {
    if (savedFilters.length > 0 && activeFilters.length === 0) {
      const defaultFilter = savedFilters.find((f) => f.isDefault);
      if (defaultFilter) {
        setActiveSavedFilterId(defaultFilter.id);
      }
    }
  }, [savedFilters, activeFilters.length]);

  return {
    activeFilters,
    setActiveFilters,
    activeSavedFilterId,
    savedFilters,
    isSavingOrUpdating,
    handleSaveFilter,
    handleUpdateFilter,
    handleDeleteSavedFilter,
    handleLoadFilter,
    handleUnloadFilter,
    handleApplyFilters,
  };
};
